import { MedplumClient } from "@medplum/core";
import {
  Organization,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  Reference,
} from "@medplum/fhirtypes";

import { Questionnaires } from "@/fhir/enums/questionnaires";
import {
  mergeOrganizationAccountFirst,
  resourceHasOrganizationLinkage,
  setAccountsBestEffort,
} from "@/utils/accounts";
import { joinReferenceString } from "@/utils/medplum";
import { isMedplumForbiddenError } from "@/utils/medplum-forbidden";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";

import { StatementSettingsFormType } from "../zod-schemas/statement-settings";

export async function fetchStatementSettingsQuestionnaire(
  medplum: MedplumClient,
): Promise<Questionnaire | undefined> {
  return await medplum.searchOne("Questionnaire", {
    url: Questionnaires.StatementSettings,
    status: "active",
  });
}

/**
 * Finds the practice-scoped QuestionnaireResponse. Statement settings are one record per
 * practice, so the newest org-compartment response with no subject wins.
 */
export async function fetchStatementSettingsResponse(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  organizationReference: string,
): Promise<QuestionnaireResponse | undefined> {
  const questionnaireReference = questionnaire.id
    ? joinReferenceString("Questionnaire", questionnaire.id)
    : undefined;
  if (!questionnaireReference) return undefined;

  const responses = await medplum.searchResources("QuestionnaireResponse", {
    questionnaire: questionnaireReference,
    _compartment: organizationReference,
    "subject:missing": true,
    _sort: "-_lastUpdated",
    _count: 1,
  });

  return responses[0];
}

/**
 * Creates or updates the practice-scoped statement settings QuestionnaireResponse.
 *
 * Mirrors the procedure-fee write path: the response carries no subject (the care provider
 * access policy only grants org-scoped settings responses when subject is missing) and links
 * to the practice through `meta.accounts`.
 */
export async function saveStatementSettingsResponse(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  values: StatementSettingsFormType,
  practitionerReference: Reference<Practitioner>,
  organizationId: string,
  existingQuestionnaireResponse?: QuestionnaireResponse,
): Promise<QuestionnaireResponse> {
  const organizationAccountRef: Reference<Organization> = {
    reference: joinReferenceString("Organization", organizationId),
  };

  let payload = getQuestionnaireResponseFromFormValues(
    questionnaire,
    values,
    undefined,
    practitionerReference,
    existingQuestionnaireResponse ?? null,
  );

  payload = {
    ...payload,
    author: practitionerReference,
    status: "completed",
    meta: {
      ...payload.meta,
      accounts: mergeOrganizationAccountFirst(payload.meta?.accounts, organizationAccountRef),
    } as QuestionnaireResponse["meta"],
  };

  const write = async (resource: QuestionnaireResponse): Promise<QuestionnaireResponse> => {
    if (existingQuestionnaireResponse?.id) {
      return await medplum.updateResource({ ...resource, id: existingQuestionnaireResponse.id });
    }
    return await medplum.createResource(resource);
  };

  try {
    return await write(payload);
  } catch (error) {
    // Non-admin billers can be blocked from writing org account linkage directly. Retry without
    // it, then let the bot apply the accounts.
    if (!isMedplumForbiddenError(error)) throw error;

    const { meta, ...rest } = payload;
    const saved = await write({
      ...rest,
      ...(meta ? { meta: { ...meta, accounts: undefined } } : {}),
    });

    // Skip a redundant $set-accounts when the write already applied org linkage. A second call
    // can normalize meta and drop client fields (e.g. meta.accounts) before downstream bots run.
    if (!resourceHasOrganizationLinkage(saved, organizationAccountRef)) {
      await setAccountsBestEffort(medplum, saved, meta?.accounts ?? [organizationAccountRef], {
        extractFromPatient: false,
      });
    }

    return saved;
  }
}
